import { Button } from "@/components/_components/button";
import { Card, CardContent } from "@/components/_components/card";
import { Skeleton } from "@/components/_components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { ROUTES, getSenderPersonaEditRoute, getSenderPersonaDetailsRoute } from "@/constants/routes";
import { AGENT_TYPE, AGENT_TYPE_LABELS, PAGINATION } from "@/constants/constants";
import { Edit, Plus, RotateCcw, Search, Trash2, Users, BarChart3, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "@/contexts/page-header-context";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/_components/badge";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useAuthorities } from "@/hooks/useAuthorities";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";

interface SenderPersonaFilter {
  name?: string;
  agentType?: string;
}

const SenderPersonaPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_SENDER_PERSONA_VIEW">
      <SenderPersonaContent />
    </PageAccessGuard>
  );
};

const SenderPersonaContent: React.FC = () => {
  const navigate = useNavigate();
  const { setHeaderData } = usePageHeader();
  const { hasAuthority } = useAuthorities();
  const [searchInput, setSearchInput] = useState("");
  const [agentTypeFilter, setAgentTypeFilter] = useState("");
  const [pageSize, setPageSize] = useState(PAGINATION.DEFAULT_PAGE_SIZE);
  const [isSearching, setIsSearching] = useState(false);
  const currentFilterRef = useRef<SenderPersonaFilter>({});
  const initialLoadRef = useRef(false);

  const canUpdate = hasAuthority('ADMIN_SENDER_PERSONA_UPDATE');

  const paginatedQuery = usePaginatedQuery<SenderPersonaFilter, any[]>(
    (filter, options) => api.getAllSenderPersonas(filter, options),
    { pageSize }
  );

  const {
    data: personas,
    isPending: isLoading,
    executeQuery,
    currentPage,
    totalCount: total,
    setCurrentPage,
    currentFilter,
  } = paginatedQuery;

  // Clear isSearching once the query finishes
  useEffect(() => {
    if (!isLoading) setIsSearching(false);
  }, [isLoading]);

  const handleSearch = useCallback(() => {
    const filter: SenderPersonaFilter = {
      ...(searchInput.trim() && { name: searchInput.trim() }),
      ...(agentTypeFilter && agentTypeFilter !== "all" && { agentType: agentTypeFilter }),
    };
    currentFilterRef.current = filter;
    setIsSearching(true);
    executeQuery(filter, 1);
  }, [searchInput, agentTypeFilter, executeQuery]);

  const handleReset = useCallback(() => {
    setSearchInput("");
    setAgentTypeFilter("");
    const filter: SenderPersonaFilter = {};
    currentFilterRef.current = filter;
    setIsSearching(true);
    executeQuery(filter, 1);
    toast.success("Form reset successfully");
  }, [executeQuery]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this sender persona?")) return;
    try {
      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.SENDER_PERSONA}/${id}`,
        method: "DELETE",
      });
      toast.success("Sender persona deleted successfully");
      executeQuery(currentFilterRef.current, currentPage);
    } catch (error: any) {
      console.error("Error deleting sender persona:", error);
      toast.error("Failed to delete sender persona");
    }
  };

  // Initial load
  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    const filter: SenderPersonaFilter = {};
    currentFilterRef.current = filter;
    executeQuery(filter, 1);
  }, []);

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: personas !== undefined ? {
        count: total,
        label: 'persona',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
      actions: canUpdate ? (
        <Button
          onClick={() => navigate(ROUTES.SENDER_PERSONA_FORM)}
          className="h-9 px-2 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-semibold text-sm rounded-md"
        >
          <Plus className="h-4 w-3" />
          Add Sender Persona
        </Button>
      ) : undefined
    });
    return () => { setHeaderData(null); };
  }, [personas, total, canUpdate, setHeaderData, navigate]);

  if (isLoading && !personas) {
    return (
      <div className="bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
            <Card className="shadow-md border-border bg-card">
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="bg-background">
      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section */}
          <div className="bg-card border border-border/50 rounded-lg mb-2">
            <div className="px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find personas by agent type and name
                  </p>
                </div>
              </div>
            </div>

            <div className="px-4 pb-0">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                    Agent Type
                  </label>
                  <Select value={agentTypeFilter} onValueChange={setAgentTypeFilter}>
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agent Types</SelectItem>
                      {AGENT_TYPE.map((type) => (
                        <SelectItem key={type} value={type}>
                          {AGENT_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="name" className="block text-sm font-semibold text-foreground">
                    Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Search by name"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={isSearching && isLoading}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isSearching && isLoading ? (
                      <><Search className="h-4 w-4 mr-1.5 animate-pulse" />Searching...</>
                    ) : (
                      <><Search className="h-4 w-4 mr-1.5" />Search</>
                    )}
                  </Button>
                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {personas && personas.length === 0 ? (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Sender Personas Found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchInput || agentTypeFilter
                      ? "No sender personas found matching your search criteria"
                      : "No sender personas yet. Create one to get started."}
                  </p>
                  <div className="flex gap-2 justify-center">
                    {(searchInput || agentTypeFilter) && (
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                    {canUpdate && (
                      <Button
                        onClick={() => navigate(ROUTES.SENDER_PERSONA_FORM)}
                        className="bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Sender Persona
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-border/50 bg-card backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">Sender Persona Results</h2>
                    <Badge variant="secondary" className="text-xs font-medium">
                      {total} {total === 1 ? 'record' : 'records'}
                    </Badge>
                    <span className="text-muted-foreground/60">•</span>
                    <p className="text-sm text-muted-foreground">
                      Manage your sender personas and track configurations
                    </p>
                  </div>
                </div>

                <div className="w-full">
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                        <TableRow>
                          <TableHead className="font-semibold">Sl No</TableHead>
                          <TableHead className="font-semibold">Agent Type</TableHead>
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold">AI Identifier</TableHead>
                          <TableHead className="font-semibold">Language</TableHead>
                          <TableHead className="font-semibold">Email</TableHead>
                          <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(personas || []).map((persona, index) => {
                          const serialNumber = (currentPage - 1) * pageSize + index + 1;
                          return (
                            <TableRow
                              key={persona.id}
                              className="transition-colors hover:bg-muted/50 cursor-pointer"
                            >
                              <TableCell className="font-medium">{serialNumber}</TableCell>
                              <TableCell>{AGENT_TYPE_LABELS[persona.agentType as keyof typeof AGENT_TYPE_LABELS] || persona.agentType}</TableCell>
                              <TableCell className="font-medium">{persona.name}</TableCell>
                              <TableCell>{persona.aiIdentifier}</TableCell>
                              <TableCell>{persona.languagePreference}</TableCell>
                              <TableCell>{persona.emailAddress}</TableCell>
                              <TableCell>
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                    onClick={(e) => { e.stopPropagation(); navigate(getSenderPersonaDetailsRoute(persona.id!)); }}
                                    title="View Sender Persona"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {canUpdate && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); navigate(getSenderPersonaEditRoute(persona.id!)); }}
                                        title="Edit Sender Persona"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(persona.id!); }}
                                        title="Delete Sender Persona"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 mt-4">
                  <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                    {total} {total === 1 ? 'record' : 'records'}
                  </div>
                  <div className="flex w-full items-center gap-8 lg:w-fit">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => {
                          const newSize = Number(value);
                          setPageSize(newSize);
                          if (currentFilter) {
                            executeQuery(currentFilter, 1, newSize);
                          } else {
                            setCurrentPage(1);
                          }
                        }}
                        disabled={isSearching && isLoading}
                      >
                        <SelectTrigger className="h-8 w-[70px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                            <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1 || (isSearching && isLoading)}
                          title="First page"
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1 || (isSearching && isLoading)}
                          title="Previous page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage >= totalPages || (isSearching && isLoading)}
                          title="Next page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage >= totalPages || (isSearching && isLoading)}
                          title="Last page"
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SenderPersonaPage;
